// now this file is an artifact
import type { LocationQueryValue } from 'vue-router';
import { ApiKey, BASE_METHOD, fetchApi, FetchApiOrigin } from '~/common/API/baseApi';
import { Role } from '~/common/constants';
import { UserStatus, type UserState } from '~/interfaces/user';
import { useUserStore } from '~/store/UserModule';

type LoginAuthorizeBody = {
  login: string;
  password: string;
};

export type BindMessenger = 'telegram' | 'max';

/**
 * Ответ check-auth. Пока привязка не завершена, бэк отдаёт пустой профиль
 * (id: 0, accessToken: ""), поэтому «готово» определяем по непустым полям.
 *
 * После объединения учёток id может отличаться от того, под которым
 * пользователь сидел до привязки: выживает одна учётка, и accessToken с
 * профилем нужно перечитать, а не считать прежними.
 */
export type MessengerBindResult = {
  id?: number;
  accessToken?: string;
  name?: string;
  avatar?: string | null;
  email?: string;
  emailIsFake?: boolean | null;
  telegram?: string;
  telegramId?: string;
  max?: string | null;
  maxId?: string | null;
  roles?: number[] | null;
  [key: string]: any;
};

export default class AuthService {
  static authKey = '';

  static user: UserState | null = null;

  static contextUserId: number | null = null;

  // static authorize = async (body: LoginAuthorizeBody) => {
  //   const { 'x-api-token': token } = (await fetchApi({
  //     body,
  //     origin: FetchApiOrigin.DATA_API_ORIGIN,
  //     method: BASE_METHOD.POST,
  //     endpoint: '/auth/login',
  //   })) as AuthorizeResponse;
  //   ApiKey.set(token);
  // };

  // Ключ для входа через бота. Ручки у мессенджеров раздельные —
  // общей /user/messenger/bot/generate-auth-key у бэка нет.
  static generateAuthKey = async (messenger: BindMessenger = 'telegram') => {
    const { authKey } = (await fetchApi({
      method: BASE_METHOD.POST,
      endpoint: `/user/${messenger}/bot/generate-auth-key`,
      isFullScreenLoading: false,
    })) as { authKey: string };
    return authKey;
  };

  static checkTelegramAuthorize = async (authKey: string) => {
    const { id, accessToken } = (await fetchApi({
      endpoint: '/user/telegram/bot/check-auth',
      params: { authKey },
      isFullScreenLoading: false,
    })) as Partial<{ id: number; accessToken: string }>;

    if (!id || !accessToken) throw new Error('');
    ApiKey.set(accessToken);
    useCookie("authorizedP").value = accessToken;
    return { id, accessToken };
  };

  static getUser = async () => {
    const response = await fetchApi({
      endpoint: "/user",
      method: BASE_METHOD.GET,
      isFullScreenLoading: false,
    });
    return response as UserState;
  };

  static updateUser = async (body: UserState) => {
    const response = await fetchApi({
      endpoint: "/user/update",
      method: BASE_METHOD.PUT,
      body,
      params: {
        id: body.id,
      },
      isFullScreenLoading: false,
    });
    return response as UserState;
  };

  static setPassword = async (body: {
    id: number,
    password: string
  }) => {
    const response = await fetchApi({
      endpoint: "/admin/user/set-password",
      method: BASE_METHOD.POST,
      body,
      isFullScreenLoading: false,
    });
    return response as UserState;
  };

  static checkEmail = async (email: string) => {
    const response = await fetchApi({
      endpoint: '/user/email/check',
      method: BASE_METHOD.GET,
      params: { email },
      isFullScreenLoading: false,
    });
    return response as { available: boolean };
  };

  static registerStart = async (email: string) => {
    const response = await fetchApi({
      endpoint: '/user/registration/email/start',
      method: BASE_METHOD.POST,
      body: { identifier: email, role: 10 },
      isFullScreenLoading: false,
    });
    return response as { id: number };
  };

  static registerStep2 = async (id: number, password: string) => {
    const response = await fetchApi({
      endpoint: '/user/registration/email/step2',
      method: BASE_METHOD.POST,
      body: { id, password },
      isFullScreenLoading: false,
    });
    return response as { token?: string; expiredAt?: string };
  };

  static confirmRegistration = async (code: string) => {
    const response = await fetchApi({
      endpoint: '/user/registration/email/confirm',
      method: BASE_METHOD.POST,
      body: { code },
      isFullScreenLoading: false,
    });
    return response;
  };

  static passwordConfirm = async (code: string) => {
    const response = await fetchApi({
      endpoint: '/user/password/confirm',
      method: BASE_METHOD.POST,
      body: { code },
      isFullScreenLoading: false,
    });
    return response;
  };

  static passwordRequest = async (email: string) => {
    const response = await fetchApi({
      endpoint: '/user/password/request',
      method: BASE_METHOD.POST,
      body: { email, role: 10 },
      isFullScreenLoading: false,
    });
    return response as { token: string; expiredAt: string };
  };

  static loginEmail = async (username: string, password: string) => {
    const response = await fetchApi({
      endpoint: '/user/advertiser/login',
      method: BASE_METHOD.POST,
      body: { username, password },
      isFullScreenLoading: false,
    });
    return response as { id: number; accessToken: string };
  };

  // Привязка идёт к пользователю из токена. Роут общий для мессенджеров,
  // старый /user/telegram/bot/user-bind-start ведёт туда же и оставлен живым.
  // Повторный вызов чаще раза в 30 секунд отдаёт код 1307.
  static messengerBindStart = async (messenger: BindMessenger) => {
    const response = await fetchApi({
      endpoint: '/user/messenger/bot/user-bind-start',
      method: BASE_METHOD.POST,
      body: { messenger },
      isFullScreenLoading: false,
    });
    return response as { authKey: string };
  };

  // Поллинг раздельный: общей ручки /user/messenger/bot/check-auth нет.
  // Здесь же приезжают ошибки самой привязки — она завершается в колбэке бота,
  // а не в ответе на bind-start.
  static messengerBindCheck = async (messenger: BindMessenger, authKey: string) => {
    const response = await fetchApi({
      endpoint: `/user/${messenger}/bot/check-auth`,
      method: BASE_METHOD.GET,
      params: { authKey },
      isFullScreenLoading: false,
    });
    return response as MessengerBindResult;
  };

  static telegramBindStart = async () => AuthService.messengerBindStart('telegram');

  static telegramBindCheck = async (authKey: string) =>
    AuthService.messengerBindCheck('telegram', authKey);
}
